import { ValidationResult } from 'joi';

import { ValidationFunctionResult } from '../../../../shared/types/validation-function-result';
import { setStatusSchema } from '../../../../shared/validators/schemas/set-status.schema';
import { TenantRecordDto } from '../../dtos/tenant-record.dto';
import { TenantActionEnum } from '../../enums/tenant-action.enum';
import { TenantStatusEnum } from '../../enums/tenant-status.enum';

/**
 * Validates if an action can be performed by validating the record data.
 * @param record - The record for the data validation.
 * @param action - The action to be validated.
 * @param abortEarly - Whether to abort the validation on the first error.
 * @returns The validation result.
 */
export const canPerformAction = (
  record: TenantRecordDto,
  action: TenantActionEnum,
  abortEarly: boolean = false,
): ValidationFunctionResult => {
  // Check if record is provided
  if (!record) throw new Error('record is required.');

  let validationResult: ValidationResult | undefined;

  const fixedStatuses = [
    TenantStatusEnum.ACTIVE,
    TenantStatusEnum.PAST_DUE,
    TenantStatusEnum.UNPAID,
  ];

  switch (action) {
    case TenantActionEnum.ACTIVATE:
      validationResult = setStatusSchema(
        TenantStatusEnum.ACTIVE,
        fixedStatuses,
      ).validate(record, {
        abortEarly,
      });
      break;

    case TenantActionEnum.PUT_ON_NOTICE_PAYMENT:
      validationResult = setStatusSchema(
        TenantStatusEnum.PAST_DUE,
        fixedStatuses,
      ).validate(record, {
        abortEarly,
      });
      break;

    case TenantActionEnum.PUT_ON_HOLD:
      validationResult = setStatusSchema(
        TenantStatusEnum.UNPAID,
        fixedStatuses,
      ).validate(record, {
        abortEarly,
      });
      break;

    case TenantActionEnum.DEACTIVATE:
      validationResult = setStatusSchema(
        TenantStatusEnum.PAUSED,
        fixedStatuses,
      ).validate(record, {
        abortEarly,
      });
      break;

    case TenantActionEnum.TERMINATE:
      validationResult = setStatusSchema(
        TenantStatusEnum.CANCELED,
        fixedStatuses,
      ).validate(record, {
        abortEarly,
      });
      break;

    default:
      throw new Error(`Unknown action: ${action}`);
  }

  // Check for validation errors
  if (validationResult?.error) {
    return {
      isValid: false,
      errors: validationResult.error.details?.map((e) => e.message),
    };
  }

  // Return valid result if no errors
  return { isValid: true };
};
